import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { historyKeys } from './historyApi';
import { mealPatternKeys } from './mealPatternApi';
import { foodAllergyKeys } from './foodAllergyApi';
import type {
  AdjustmentItem,
  AdjustmentChildItem,
  AdjustmentException,
  AdjustmentOnMenu,
  AdjustmentDietOrder,
} from '../types/mealPattern';

// --- Query Keys ---

export const adjustmentKeys = {
  all: ['adjustment'] as const,
  byPatient: (facilityId: number, patientId: number) =>
    [...adjustmentKeys.all, 'byPatient', facilityId, patientId] as const,
  children: (facilityId: number, patientId: number) =>
    [...adjustmentKeys.all, 'children', facilityId, patientId] as const,
  exceptions: (facilityId: number, patientId: number) =>
    [...adjustmentKeys.all, 'exceptions', facilityId, patientId] as const,
  onMenu: (facilityId: number, patientId: number) =>
    [...adjustmentKeys.all, 'onMenu', facilityId, patientId] as const,
  dietOrder: (facilityId: number, patientId: number) =>
    [...adjustmentKeys.all, 'dietOrder', facilityId, patientId] as const,
};

/**
 * The Meal Pattern grid's conflict triangle (`adjustmentWarning` /
 * `adjustmentString`) is computed by the API from the resident's ACTIVE
 * adjustments, their exceptions and their child counts
 * (MealPatternDAL.PopulateAdjustmentWarnings). Both sections live on
 * the same resident page, so every adjustment write must refetch the
 * meal pattern too, or a deactivated / deleted Never Serve keeps its
 * warning until the 5-minute stale window passes.
 */
export function invalidateMealPatternWarnings(
  queryClient: QueryClient,
  facilityId: number,
  patientId: number,
) {
  queryClient.invalidateQueries({ queryKey: mealPatternKeys.byPatient(facilityId, patientId) });
  // The Meal Pattern tab's "Missing NeverServe" allergy banner depends on which
  // adjustments exist. WinForms re-runs CheckFoodAllergyWarnings after every
  // adjustment save and delete (frmDietech_Prd_Meal_Pattern.vb:16720).
  queryClient.invalidateQueries({ queryKey: foodAllergyKeys.warnings(facilityId, patientId) });
}

// --- Queries ---

export function useAdjustments(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: adjustmentKeys.byPatient(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<AdjustmentItem[]>(
        API.adjustmentByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load adjustments', 'error'),
    },
  });
}

export function useAdjustmentChildren(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: adjustmentKeys.children(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<AdjustmentChildItem[]>(
        API.adjustmentChildrenByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load adjustment children', 'error'),
    },
  });
}

export function useAdjustmentExceptions(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: adjustmentKeys.exceptions(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<AdjustmentException[]>(
        API.adjustmentExceptionsByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load adjustment exceptions', 'error'),
    },
  });
}

export function useAdjustmentOnMenu(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: adjustmentKeys.onMenu(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<AdjustmentOnMenu[]>(
        API.adjustmentOnMenuByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load adjustment on-menu items', 'error'),
    },
  });
}

export function useAdjustmentDietOrder(facilityId: number, patientId: number) {
  const { addNotification } = useNotificationStore();

  return useQuery({
    queryKey: adjustmentKeys.dietOrder(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<AdjustmentDietOrder[]>(
        API.adjustmentDietOrderByPatient(facilityId, patientId)
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    meta: {
      onError: () => addNotification('Failed to load adjustment diet order', 'error'),
    },
  });
}

// --- Mutations ---

/**
 * Patient adjustment save. The dialog operates on a
 * GlobalAdjustment-shaped form (forked from the global dialog), so
 * the payload's `item` is in that shape — but the save route depends
 * on whether the original row was patient-owned (legacy
 * `tblPrd_Adjustment`) or a global-instance row in
 * `tblPrd_Adjustment_Global` (where FKey_Patient is set). The
 * `patientItem` field carries the original AdjustmentItem so the
 * server can read the `isGlobal` flag and the correct PKey for the
 * UPDATE target. New rows always go to the legacy table.
 *
 * Child collections follow the null/[]/[..] convention from the
 * global side:
 *   null → leave existing rows untouched
 *   []   → clear all
 *   [..] → replace with this set (UPSERT)
 */
export interface AdjustmentSavePayload {
  item: unknown; // GlobalAdjustment-shaped form payload (see dialog)
  patientItem: AdjustmentItem | null;
  exceptions?: unknown[] | null;
  onMenu?: unknown[] | null;
  children?: unknown[] | null;
  dietOrder?: unknown[] | null;
  /**
   * The edit changes what a linked global copy targets (Type, Action, Item,
   * Allergy Group, Menu Category or Food Group): the server breaks the
   * global link and saves to the resident's own tables in one transaction,
   * as WinForms does ("Changes made will remove the global link."). The
   * server returns the new resident-only row's key.
   */
  breakGlobalLink?: boolean;
}

export function useSaveAdjustment(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: AdjustmentSavePayload) => {
      // Route hints — server uses these to pick the right table and
      // whether to INSERT vs UPDATE. For a new row both PKeys are 0
      // and isGlobal=false → INSERT into tblPrd_Adjustment.
      const isGlobal = payload.patientItem?.isGlobal ?? false;
      const originalPKey = payload.patientItem?.pKey_Prd_Adjustment ?? 0;
      const originalPKeyGlobal = payload.patientItem?.pKey_Prd_Adjustment_Global ?? 0;

      const { data } = await apiClient.post<number>(
        API.adjustmentAddMod(facilityId),
        {
          Item: payload.item,
          PatientId: patientId,
          IsGlobal: isGlobal,
          OriginalPKey: originalPKey,
          OriginalPKeyGlobal: originalPKeyGlobal,
          Exceptions: payload.exceptions ?? null,
          OnMenu: payload.onMenu ?? null,
          Children: payload.children ?? null,
          DietOrder: payload.dietOrder ?? null,
          ...(payload.breakGlobalLink && isGlobal ? { BreakGlobalLink: true } : {}),
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
    },
    onError: (err) => {
      // 409: the server refused the save and said why (a linked global
      // copy's exceptions can't change).
      const res = (err as { response?: { status?: number; data?: { error?: unknown } } })?.response;
      const why = res?.status === 409 && typeof res.data?.error === 'string' ? res.data.error : null;
      addNotification(why ?? 'Failed to save adjustment', 'error');
    },
  });
}

/**
 * Swap the Priority values of two patient adjustments. Each pKey is
 * the SYNTHETIC patient PKey (positive = legacy row, negative =
 * global-instance). Used by the Up/Down toolbar buttons; callers
 * should ensure both rows are in the same ActionType group.
 */
export function useSwapAdjustmentPriority(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (payload: { pKeyA: number; pKeyB: number }) => {
      await apiClient.post(API.adjustmentSwapPriority(facilityId, patientId), {
        PKeyA: payload.pKeyA,
        PKeyB: payload.pKeyB,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
    onError: () => {
      addNotification('Failed to reorder adjustment', 'error');
    },
  });
}

/**
 * Bulk activate / deactivate every patient adjustment row (legacy +
 * global-instance). Pass `true` to clear the Inactive flag, `false`
 * to set it. Returns the affected row count.
 */
/**
 * Single-row Inactive toggle — per-row counterpart of
 * `useBulkActivateAdjustments`. Used by the toolbar
 * Activate/Deactivate split-button's main click. Server takes
 * the row's real positive PKey + isGlobal flag and writes
 * Inactive on the matching row in tblPrd_Adjustment or
 * tblPrd_Adjustment_Global. No log written here — logging will
 * be wired separately.
 */
export function useToggleAdjustmentInactive(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (input: { pKey: number; isGlobal: boolean; newInactive: boolean }) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentToggleInactive(facilityId, patientId),
        {
          pKey: input.pKey,
          isGlobal: input.isGlobal,
          newInactive: input.newInactive,
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
    onError: () => {
      addNotification('Failed to toggle Inactive', 'error');
    },
  });
}

export function useBulkActivateAdjustments(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (activate: boolean) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentBulkActivate(facilityId, patientId, activate),
      );
      return data;
    },
    onSuccess: (count, activate) => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      addNotification(`${count} adjustments ${activate ? 'activated' : 'deactivated'}`, 'success');
    },
    onError: () => {
      addNotification('Bulk operation failed', 'error');
    },
  });
}

/**
 * Promote a patient-only legacy row into a patient-instance of a
 * freshly-saved global template. Server runs in one transaction:
 * deletes the legacy row + its child rows, then INSERTs a
 * patient-instance lineage-linked to the new template (template
 * children cascade into the _Global child tables under the new
 * instance via the same machinery as InsertPatientFromTemplate).
 * Returns the new instance PKey.
 */
export function useMakeIntoGlobalAdjustment(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (args: { legacyPKey: number; templatePKey: number }) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentMakeIntoGlobal(facilityId, patientId, args.legacyPKey, args.templatePKey),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
      addNotification('Adjustment made into global', 'success');
    },
    onError: () => {
      addNotification('Failed to make adjustment into global', 'error');
    },
  });
}

/**
 * Sever the lineage link between a patient global-instance row and
 * its template. Server clones the instance + its _Global child rows
 * into the legacy patient tables (preserving Priority + ActionType
 * so the grid slot stays stable) and DELETEs the original instance
 * + its _Global child rows. The template + sibling patients'
 * instances are untouched. Returns the new legacy PKey.
 */
export function useBreakGlobalAdjustmentLink(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKeyGlobal: number) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentBreakGlobalLink(facilityId, patientId, pKeyGlobal),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
      addNotification('Global link broken', 'success');
    },
    onError: () => {
      addNotification('Failed to break global link', 'error');
    },
  });
}

/**
 * Insert a patient-instance row that lineage-links to an existing
 * global adjustment template. Clones the template's scalar fields
 * into a new row in tblPrd_Adjustment_Global with FKey_Patient set
 * and FKey_Prd_Adjustment_Global = templatePKey. Children cascade.
 * Used after the Global → New From Existing flow saves the new
 * template so the current patient ends up linked to it.
 */
export function useInsertPatientAdjustmentFromTemplate(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (templatePKey: number) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentInsertPatientFromTemplate(facilityId, patientId, templatePKey),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to link new template to patient', 'error');
    },
  });
}

/**
 * Copy every adjustment row + child tables from one resident to
 * another in a single server transaction. Legacy rows clone into
 * tblPrd_Adjustment; global-instance rows clone into
 * tblPrd_Adjustment_Global preserving their template lineage so the
 * new patient stays linked to the same global template. Returns the
 * count of parent rows copied.
 */
export function useCopyAdjustmentsFromResident(facilityId: number, toPatientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (fromPatientId: number) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentCopyFromResident(facilityId, toPatientId, fromPatientId),
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, toPatientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, toPatientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, toPatientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, toPatientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, toPatientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, toPatientId) });
    },
    onError: () => {
      addNotification('Failed to copy adjustments', 'error');
    },
  });
}

/**
 * Delete every patient adjustment row (legacy + global-instance) and
 * cascade-delete all child tables. Template rows and sibling
 * patients are untouched.
 */
export function useDeleteAllAdjustments(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.delete<number>(
        API.adjustmentDeleteAll(facilityId, patientId),
      );
      return data;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: historyKeys.adjDeleted(facilityId, patientId) });
      addNotification(`${count} adjustments deleted`, 'success');
    },
    onError: () => {
      addNotification('Failed to delete adjustments', 'error');
    },
  });
}

export function useDeleteAdjustment(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      // pKey is the SYNTHETIC patient PKey: positive for legacy
      // tblPrd_Adjustment rows, negative for global-instance rows
      // (where the absolute value is the real PKey_Prd_Adjustment_Global).
      // The server routes by sign.
      await apiClient.delete(API.adjustmentDelete(facilityId, patientId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: historyKeys.adjDeleted(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to delete adjustment', 'error');
    },
  });
}

// --- Exception Mutations ---

export function useAddException(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (item: AdjustmentException) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentExceptionAdd(facilityId),
        item,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
    onError: () => {
      addNotification('Failed to add exception', 'error');
    },
  });
}

export function useDeleteException(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { fKeyAdj: number; fKeyItem: number; type: number }) => {
      await apiClient.delete(
        API.adjustmentExceptionDelete(facilityId, params.fKeyAdj, params.fKeyItem, params.type),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
  });
}

// --- Child Item Mutations ---

export function useAddAdjustmentChild(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (item: AdjustmentChildItem) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentChildAdd(facilityId),
        item,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
    onError: () => {
      addNotification('Failed to add child item', 'error');
    },
  });
}

export function useDeleteAdjustmentChild(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.adjustmentChildDelete(facilityId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: historyKeys.adjDeleted(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
    },
    onError: () => {
      addNotification('Failed to delete child item', 'error');
    },
  });
}

// --- OnMenu Mutations ---

export function useAddOnMenu(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (item: AdjustmentOnMenu) => {
      const { data } = await apiClient.post<number>(
        API.adjustmentOnMenuAdd(facilityId),
        item,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to add on-menu condition', 'error');
    },
  });
}

export function useDeleteOnMenu(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pKey: number) => {
      await apiClient.delete(API.adjustmentOnMenuDelete(facilityId, pKey));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
    },
  });
}

/**
 * Add-snapshot log — mirrors the WinForms write that fires right
 * after a new adjustment row is saved:
 *
 *   Dim adjLogData As New Adjustment_Log.Adjustment_LogData
 *   With adjLogData
 *       .rowAdjOriginal     = rowLoad   (the just-saved row)
 *       .rowAdjModified     = Nothing
 *       .dv*Original        = filtered from dsPrd_Adjustement on
 *                             FKey_Prd_Adjustment = new PKey
 *   End With
 *   Dim adjLog As New Adjustment_Log(g_ConnectionUserManager,
 *       adjLogData, False, isGlobal, menuDaysChanged,
 *       AIMRjectionType.None)
 *   adjLog.InsertAdjLog(LogMethod.ByUser)
 *
 * Server returns 200 unconditionally; failures are swallowed so a
 * snapshot-log error never undoes the user's save.
 */
export function useLogAdjustmentAdd(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: { parentPKey: number; isGlobal: boolean; daysChanged: boolean }) => {
      await apiClient.post(
        API.adjLogAdd(facilityId, patientId, input.parentPKey),
        { isGlobal: input.isGlobal, daysChanged: input.daysChanged },
      );
    },
  });
}

/**
 * Modify-snapshot log — mirrors the WinForms write that compares
 * the row + all five child collections at dialog-open time
 * against the post-save state on disk:
 *
 *   .rowAdjOriginal     = rowOriginal       (pre-edit, from client)
 *   .rowAdjModified     = rowLoadCopy       (post-save, server reads from DB)
 *   .dv*Original        = pre-edit views    (from client payload)
 *   .dv*Modified        = filtered from dsPrd_Adjustement post-save
 *
 * Caller MUST send the pre-edit parent + all five child
 * collections captured when the dialog opened. The server reads
 * the current state from DB and lets the legacy class compute
 * the diff. Children/exceptions/onMenu are sent whole; the DF
 * table is split into two halves on the client (consistencies
 * have FKey_Diet_Type null; diet types have FKey_Consist null) so
 * each half flows to the matching legacy DataView.
 */
/**
 * Delete snapshot log — mirrors the WinForms write that fires
 * with isDelete=true, menuDaysChanged=true, and no Modified
 * views. Caller MUST snapshot the row + all 5 child collections
 * BEFORE the delete mutation fires (the live rows are gone from
 * DB by the time the log runs).
 *
 * isDelete=true on the server flips the transaction type to
 * `Deleted_Adjustment` so the row appears in the Deleted Items
 * view and can be restored.
 */
export function useLogAdjustmentDelete(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      isGlobal: boolean;
      originalParent: Record<string, unknown>;
      originalChildren: Array<Record<string, unknown>>;
      originalExceptions: Array<Record<string, unknown>>;
      originalOnMenu: Array<Record<string, unknown>>;
      originalConsistencies: Array<Record<string, unknown>>;
      originalDietTypes: Array<Record<string, unknown>>;
    }) => {
      await apiClient.post(
        API.adjLogDelete(facilityId, patientId),
        {
          isGlobal: input.isGlobal,
          originalParent: input.originalParent,
          originalChildren: input.originalChildren,
          originalExceptions: input.originalExceptions,
          originalOnMenu: input.originalOnMenu,
          originalConsistencies: input.originalConsistencies,
          originalDietTypes: input.originalDietTypes,
        },
      );
    },
  });
}

/**
 * Delete-All snapshot log — mirrors the WinForms two-pass loop
 * (legacy rows, then global instance rows) but in a single batch
 * call. One snapshot per affected row; menuDaysChanged=false on
 * every row (single-row Delete passes true).
 *
 * Caller MUST snapshot every affected row + its 5 child
 * collections from React Query cache BEFORE firing DeleteAll —
 * the live rows are gone by log time. Each entry's `isGlobal`
 * flag selects which table family the server seeds its empty
 * schema against.
 */
export function useLogAdjustmentDeleteAll(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      entries: Array<{
        isGlobal: boolean;
        originalParent: Record<string, unknown>;
        originalChildren: Array<Record<string, unknown>>;
        originalExceptions: Array<Record<string, unknown>>;
        originalOnMenu: Array<Record<string, unknown>>;
        originalConsistencies: Array<Record<string, unknown>>;
        originalDietTypes: Array<Record<string, unknown>>;
      }>;
    }) => {
      await apiClient.post(
        API.adjLogDeleteAll(facilityId, patientId),
        { entries: input.entries },
      );
    },
  });
}

/**
 * Inactive-toggle snapshot log — shared shape between the
 * single-row Activate/Deactivate toolbar click and the bulk
 * Activate All / Deactivate All:
 *
 *   .rowAdjModified  = post-toggle row (server reads from DB)
 *   .rowAdjOriginal  = clone with Inactive flipped back
 *   .dv*Modified === .dv*Original   (toggle doesn't touch
 *                                    children/exceptions/onMenu/DF)
 *   isDelete=false, isGlobal per entry, menuDaysChanged=false
 *   InsertAdjLog(LogMethod.ByUser)
 *
 * Caller fires this AFTER the toggle/bulk UPDATE succeeds.
 * - Single toggle: 1 entry containing the just-toggled row's
 *   real positive PKey + isGlobal.
 * - Bulk Activate/Deactivate All: one entry per affected row
 *   (the AdjustmentsTab's `adjustments` array gives us the
 *   per-row isGlobal + the real PKey via Math.abs of the
 *   synthetic-negative encoding).
 */
export function useLogAdjustmentInactiveToggle(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      newInactiveValue: boolean;
      entries: Array<{ pKey: number; isGlobal: boolean }>;
    }) => {
      if (!input.entries || input.entries.length === 0) return;
      await apiClient.post(
        API.adjLogInactiveToggle(facilityId, patientId),
        {
          newInactiveValue: input.newInactiveValue,
          entries: input.entries,
        },
      );
    },
  });
}

/**
 * Add-Global-Adjustments snapshot log — mirrors the WinForms
 * loop fired after the per-template insert returns:
 *
 *   For each new patient-instance:
 *     Adjustment_Log(g_ConnectionUserManager, adjLogData, False,
 *                    True, False, AIMRjectionType.None)
 *     adjLog.InsertAdjLog(LogMethod.ByUser)
 *
 * Server reads each new patient-instance row + its 5 child
 * collections from DB by PKey, so the client just needs to
 * forward the PKey list returned by the batched insert.
 *
 * isGlobal=true and menuDaysChanged=false are server-side
 * defaults — no React-side toggles needed.
 */
/**
 * Adjustment_Log.LogMethod enum mirrored for client use. See
 * the legacy VB class for the canonical values:
 *   - ByAIM    = 0 (Auto-Intelligent Menu drop — not used yet)
 *   - ByUser   = 1 (default — user explicitly picked templates)
 *   - ByGlobal = 2 (NFE — row dropped as part of template-create)
 *   - Custom   = 3 (not used yet)
 */
export const ADJ_LOG_METHOD = {
  ByAIM: 0,
  ByUser: 1,
  ByGlobal: 2,
  Custom: 3,
} as const;

export function useLogAdjustmentAddGlobal(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: { newInstancePKeys: number[]; logMethod?: number }) => {
      if (!input.newInstancePKeys || input.newInstancePKeys.length === 0) return;
      await apiClient.post(
        API.adjLogAddGlobal(facilityId, patientId),
        {
          newInstancePKeys: input.newInstancePKeys,
          // Default to ByUser (Add Global Adjustments semantics).
          // NFE callers should pass ADJ_LOG_METHOD.ByGlobal.
          logMethod: input.logMethod ?? ADJ_LOG_METHOD.ByUser,
        },
      );
    },
  });
}

/**
 * Reset a patient-instance global adjustment row to its template.
 * Server overwrites structural fields on the instance with the
 * template's values + replaces all 4 child collections wholesale.
 * Patient-specific fields (StartDate / EndDate / *_Locked /
 * AdjustmentNotes / MenuDays / InvertDays / Ignore* / ChildItemType)
 * stay untouched.
 *
 * Caller passes the REAL positive `pKey_Prd_Adjustment_Global`
 * (Math.abs the grid's synthetic-negative encoding).
 */
export function useResetToGlobalAdjustment(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();
  return useMutation({
    mutationFn: async (globalInstancePKey: number) => {
      await apiClient.post(API.adjustmentResetToGlobal(facilityId, globalInstancePKey));
      return globalInstancePKey;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.byPatient(facilityId, patientId) });
      invalidateMealPatternWarnings(queryClient, facilityId, patientId);
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.children(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.exceptions(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.onMenu(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.dietOrder(facilityId, patientId) });
    },
    onError: () => {
      addNotification('Failed to reset to global', 'error');
    },
  });
}

/**
 * Reset to Global snapshot log — mirrors the WinForms call:
 *
 *   Adjustment_Log(g_ConnectionUserManager, adjLogData, False,
 *                  True, False, AIMRjectionType.None)
 *   .InsertAdjLog(LogMethod.ByUser)
 *
 * Caller MUST snapshot the patient-instance row state from
 * React Query cache BEFORE firing the reset mutation — the live
 * row is overwritten with the template's values by the time
 * we'd read it from DB. Children are read post-reset on the
 * server and reused for both DataView halves (no child diff
 * captured — matches the WinForms RowFilter shape).
 */
export function useLogAdjustmentResetToGlobal(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      globalInstancePKey: number;
      originalParent: Record<string, unknown>;
    }) => {
      await apiClient.post(
        API.adjLogResetToGlobal(facilityId, patientId),
        {
          globalInstancePKey: input.globalInstancePKey,
          originalParent: input.originalParent,
        },
      );
    },
  });
}

/**
 * Make Into Global patient-row snapshot — mirrors the WinForms:
 *
 *   Adjustment_Log(..., isDelete=False, isGlobal=True,
 *                  menuDaysChanged=False, AIMRjectionType.None)
 *   .InsertAdjLog_ResidentToGlobal(LogMethod.ByUser)
 *
 * IMPORTANT: uses the dedicated `InsertAdjLog_ResidentToGlobal`
 * method, not the regular `InsertAdjLog`.
 *
 * Caller MUST snapshot the legacy parent row BEFORE the
 * makeIntoGlobalMutation fires — by the time this log endpoint
 * runs, the legacy row has been deleted in the same
 * transaction that created the new patient instance.
 *
 * The template-Add tblLog_Interaction row is NOT written here —
 * `GlobalAdjustmentAddModDialog` already wrote it when the
 * template was saved.
 */
export function useLogAdjustmentMakeIntoGlobal(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      newPatientInstancePKey: number;
      originalLegacyParent: Record<string, unknown>;
    }) => {
      await apiClient.post(
        API.adjLogMakeIntoGlobal(facilityId, patientId),
        {
          newPatientInstancePKey: input.newPatientInstancePKey,
          originalLegacyParent: input.originalLegacyParent,
        },
      );
    },
  });
}

export function useLogAdjustmentModify(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (input: {
      parentPKey: number;
      isGlobal: boolean;
      daysChanged: boolean;
      originalParent: Record<string, unknown>;
      originalChildren: Array<Record<string, unknown>>;
      originalExceptions: Array<Record<string, unknown>>;
      originalOnMenu: Array<Record<string, unknown>>;
      originalConsistencies: Array<Record<string, unknown>>;
      originalDietTypes: Array<Record<string, unknown>>;
    }) => {
      await apiClient.post(
        API.adjLogModify(facilityId, patientId, input.parentPKey),
        {
          isGlobal: input.isGlobal,
          daysChanged: input.daysChanged,
          originalParent: input.originalParent,
          originalChildren: input.originalChildren,
          originalExceptions: input.originalExceptions,
          originalOnMenu: input.originalOnMenu,
          originalConsistencies: input.originalConsistencies,
          originalDietTypes: input.originalDietTypes,
        },
      );
    },
  });
}
